import {
  Budget,
  CREDIT_PRICE,
  CreateBooking,
  DietaryRestriction,
  EventWithDetail,
  Language,
  PaymentMethodEnum,
  SERVICE_FEE_PRICE,
  TicketTierType,
  locationLink,
  remainTicketAmount
} from "@garther/interfaces";
import { Box, Button, Image, QuantityInput, SelectableCard, Separator } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { Calendar, MapPin, User } from "lucide-react";
import { useEffect, useState } from "react";

import { RawLayout } from "@/components";
import { bookingApiClient } from "@/libs/api/booking-client";
import { useCreditStore } from "@/stores/credit.store";

import { PaymentMethod } from "./payment-method";

interface BookingPageProps {
  event: EventWithDetail;
}

interface FormData {
  // fullName: string;
  // email: string;
  // phone: string;
  note: string;
  budget: Budget;
  languages: Language[];
  dietaryRestrictions: DietaryRestriction[];
  bringFriend: boolean;
  paymentMethod: PaymentMethodEnum;
}

export function BookingPage({ event }: BookingPageProps) {
  const { formatDate, t, formatCurrency, formatCredit } = useI18n();
  const navigate = useNavigate();
  const [selectedTicket, setSelectedTicket] = useState<string>(event.ticketTiers[0].id);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const { userCredits } = useCreditStore();

  const form = useForm<FormData>({
    defaultValues: {
      // fullName: "",
      // email: "",
      // phone: "",
      note: "",
      budget: Budget.MODERATE,
      languages: [Language.ENGLISH],
      dietaryRestrictions: [],
      bringFriend: false,
      paymentMethod: PaymentMethodEnum.CREDIT
    },
    onSubmitInvalid: ({ formApi }) => {
      const errors = Object.values(formApi.state.fieldMeta)
        .filter((field) => field.errors.length > 0)
        .map((field) => field.errors)
        .flat();
      toast.error(errors.join("\n") || t("booking.validation.general"));
    },
    onSubmit: async ({ value }) => {
      if (isProcessing) return;

      setIsProcessing(true);

      try {
        const bookingData: CreateBooking = {
          ticketTierId: selectedTicket,
          budget: value.budget,
          languages: value.languages,
          dietaryRestrictions: value.dietaryRestrictions,
          bringFriend: value.bringFriend,
          quantity: quantity,
          paymentMethod
        };

        const booking = await bookingApiClient.book(bookingData);
        toast.success(t("booking.success.bookingConfirmed"));

        navigate({
          to: `/user/$userId/booking/$bookingId`,
          params: { userId: booking.userId, bookingId: booking.id }
        });

        // if (value.paymentMethod === PaymentMethodEnum.BANK_TRANSFER) {
        //   // TODO: Handle credit payment
        // } else if (value.paymentMethod === PaymentMethodEnum.CARD) {
        //   // TODO: Handle card payment
        // }
      } catch (error) {
        console.error("Booking error:", error);
        toast.error(t("booking.error.bookingFailed"));
      } finally {
        setIsProcessing(false);
      }
    }
  });

  const bringFriend = form.useStore((state) => state.values.bringFriend);
  const paymentMethod = form.useStore((state) => state.values.paymentMethod);

  // useEffect(() => {
  //   if (curUser.profile !== null) {
  //     form.setFieldValue("fullName", curUser.profile?.name ?? "");
  //     form.setFieldValue("email", curUser.profile?.email ?? "");
  //     form.setFieldValue("phone", curUser.profile?.phone ?? "");
  //   }
  // }, [curUser.profile, form]);

  const selectedTicketType = event.ticketTiers.find((t) => t.id === selectedTicket)!;
  const subtotal = selectedTicketType.price * quantity;
  const serviceFee = subtotal > 0 ? subtotal * SERVICE_FEE_PRICE : 0;
  const total = subtotal; // + serviceFee + (bringFriend ? subtotal * SERVICE_FEE_PRICE : 0);

  // Auto-switch payment method if credit is selected but insufficient balance
  useEffect(() => {
    if (
      paymentMethod === PaymentMethodEnum.CREDIT &&
      userCredits &&
      userCredits.balance < total &&
      total > 0
    ) {
      form.setFieldValue("paymentMethod", PaymentMethodEnum.BANK_TRANSFER);
    }
  }, [paymentMethod, userCredits, total]);

  const handleQuantityChange = (delta: number) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= remainTicketAmount(event, selectedTicketType)) {
      setQuantity(newQuantity);
    }
  };

  return (
    <RawLayout>
      {/* Event Summary */}
      <div className="bg-background-secondary border-y border-border px-4 sm:px-6 lg:px-8 py-6 mt-4 md:mt-6 lg:mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-4 items-start">
            <div className="w-24 h-24 rounded-sm overflow-hidden bg-muted shrink-0 border border-border">
              <Image
                src={event.banner || event.featuredImages[0]}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <h2 className="text-text-primary font-medium text-responsive-lg">{event.title}</h2>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-text-secondary text-medium text-responsive-base">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(event.date, { year: "numeric", month: "long", day: "numeric" })}</span>
                  <span>•</span>
                  <span>{formatDate(event.date, { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div
                  className="flex items-center gap-1.5 hover:underline cursor-pointer"
                  onClick={() => {
                    if (event.location) window.open(locationLink(event.location), "_blank");
                  }}
                >
                  <MapPin className="h-5 w-5" />
                  <span>{event.location?.address || "HCM, Vietnam"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <User className="h-4 w-4" />
                  <span>{event.host.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="px-0 sm:px-2 md:px-4 lg:px-8 py-4 md:py-6 lg:py-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 lg:grid-cols-3 gap-4 lg:gap-8">
            {/* Left Column - Main Form */}
            <div className="md:col-span-3 lg:col-span-2 space-y-4 md:space-y-6">
              {/* Ticket Selection */}
              <Box className="space-y-4">
                <p className="text-text-primary font-normal text-responsive-md">
                  {t("booking.tickets.title")}
                </p>

                <div className="space-y-3">
                  {event.ticketTiers.map((ticketTier) => (
                    <SelectableCard
                      key={ticketTier.id}
                      isSelected={selectedTicket === ticketTier.id}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedTicket(ticketTier.id);
                        setQuantity(1);
                      }}
                      isDisabled={remainTicketAmount(event, ticketTier) <= 0}
                      className="flex flex-col items-start p-4 w-full"
                    >
                      <div className="flex w-full flex-col sm:flex-row justify-between item-center mb-1">
                        <p className="font-medium text-responsive-base">
                          {ticketTier.title}{" "}
                          <span className="font-normal text-responsive-base">
                            (
                            {t("booking.tickets.remain", {
                              amount: remainTicketAmount(event, ticketTier)
                            })}
                            )
                          </span>
                        </p>
                        <p className="font-normal text-responsive-base">
                          {ticketTier.price === 0
                            ? t("booking.tickets.free")
                            : formatCredit(ticketTier.price)}{" "}
                          {ticketTier.price !== 0 && (
                            <span className="text-text-secondary">{` ≈ ${formatCurrency(ticketTier.price * CREDIT_PRICE)}`}</span>
                          )}
                        </p>
                      </div>
                      <p
                        className="font-normal text-text-secondary text-responsive-base"
                        dangerouslySetInnerHTML={{ __html: ticketTier.description }}
                      />
                    </SelectableCard>
                  ))}
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center justify-between bg-muted/50 rounded-sm">
                  <span className="text-text-primary font-normal text-responsive-base">
                    {t("booking.tickets.quantity")}
                  </span>
                  <QuantityInput
                    range={{
                      min: 1,
                      max:
                        selectedTicketType.type !== TicketTierType.NORMAL
                          ? 1
                          : remainTicketAmount(event, selectedTicketType)
                    }}
                    step={1}
                    quantity={quantity}
                    handleQuantityChange={handleQuantityChange}
                  />
                </div>
                <Separator />
                <div className="flex items-center justify-between pt-2">
                  <span className="font-normal text-responsive-base">{t("booking.tickets.subtotal")}</span>
                  <span className="font-medium text-responsive-base">
                    {subtotal === 0 ? t("booking.tickets.free") : formatCredit(subtotal)}{" "}
                    {subtotal !== 0 && (
                      <span className="text-text-secondary">{` ≈ ${formatCurrency(subtotal * CREDIT_PRICE)}`}</span>
                    )}
                  </span>
                </div>
              </Box>

              {/* Attendee Information */}
              {/* <Box className="space-y-4">
                <p className="text-text-primary font-normal text-responsive-md">
                  {t("booking.attendeeInfo.title")}
                </p>

                <form.Field
                  name="fullName"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) return t("booking.validation.fullNameRequired");
                      const trimmedValue = value.trim();
                      if (trimmedValue.length < 3) return t("booking.validation.fullNameMinLength");
                      if (trimmedValue.length > 32) return t("booking.validation.fullNameMaxLength");
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <Input
                      id="fullName"
                      required
                      label={t("booking.attendeeInfo.fullName")}
                      placeholder={t("booking.attendeeInfo.fullNamePlaceholder")}
                      className="rounded-sm"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={field.state.meta.errors?.[0] || undefined}
                    />
                  )}
                </form.Field>

                <form.Field
                  name="email"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) return t("booking.validation.emailRequired");
                      if (!EMAIL_REGEX.test(value)) {
                        return t("booking.validation.emailInvalid");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <Input
                      id="email"
                      required
                      label={t("booking.attendeeInfo.email")}
                      type="email"
                      placeholder={t("booking.attendeeInfo.emailPlaceholder")}
                      className="rounded-sm"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={field.state.meta.errors?.[0] || undefined}
                    />
                  )}
                </form.Field>

                <form.Field
                  name="phone"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) return t("booking.validation.phoneRequired");
                      const trimmedValue = value.trim();
                      // Check if phone contains only digits, spaces, hyphens, and parentheses
                      if (!/^[\d\s\-()+]+$/.test(trimmedValue)) {
                        return t("booking.validation.phoneInvalid");
                      }
                      // Check minimum length (at least 8 digits)
                      const digitCount = trimmedValue.replace(/\D/g, "").length;
                      if (digitCount < 8) {
                        return t("booking.validation.phoneMinLength");
                      }
                      // Check maximum length (max 15 digits, international format)
                      if (digitCount > 15) {
                        return t("booking.validation.phoneMaxLength");
                      }
                      return undefined;
                    }
                  }}
                >
                  {(field) => (
                    <Input
                      id="phone"
                      required
                      label={t("booking.attendeeInfo.phone")}
                      type="tel"
                      placeholder={t("booking.attendeeInfo.phonePlaceholder")}
                      className="rounded-sm"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      error={field.state.meta.errors?.[0] || undefined}
                    />
                  )}
                </form.Field>

                {selectedTicketType.type !== TicketTierType.NORMAL && (
                  <>
                    <form.Field name="budget">
                      {(field) => (
                        <div>
                          <p className="text-text-secondary font-medium text-responsive-sm">
                            {t("booking.preferences.budget")}
                          </p>
                          <select
                            id="budget"
                            className="bg-transparent w-full px-3 py-2 border border-border-light rounded-non focus:outline-none focus:border-border-dark focus:bg-background-highlight pr-10 hover:cursor-pointer hover:bg-background-highlight"
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value as Budget)}
                          >
                            {PREFERENCES.budget.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {t(option.label)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </form.Field>

                    <form.Field
                      name="languages"
                      validators={{
                        onChange: ({ value }) =>
                          value.length === 0 ? t("booking.validation.languagesRequired") : undefined
                      }}
                    >
                      {(field) => (
                        <div>
                          <p className="text-text-secondary font-medium text-responsive-sm">
                            {t("booking.preferences.languages")}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {PREFERENCES.languages.options.map((lang) => (
                              <label key={lang.value} className="flex items-center space-x-1">
                                <input
                                  type="checkbox"
                                  checked={field.state.value.includes(lang.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      field.handleChange([...field.state.value, lang.value]);
                                    } else {
                                      field.handleChange(field.state.value.filter((l) => l !== lang.value));
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">{t(lang.label)}</span>
                              </label>
                            ))}
                          </div>
                          {field.state.meta.errors[0] && (
                            <p className="text-sm text-red-500">{field.state.meta.errors[0]}</p>
                          )}
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="dietaryRestrictions">
                      {(field) => (
                        <div>
                          <p className="text-text-secondary font-medium text-responsive-sm">
                            {t("booking.preferences.dietaryRestrictions")}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {PREFERENCES.dietaryRestrictions.options.map((restriction) => (
                              <label key={restriction.value} className="flex items-center space-x-1">
                                <input
                                  type="checkbox"
                                  checked={field.state.value.includes(restriction.value)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      field.handleChange([...field.state.value, restriction.value]);
                                    } else {
                                      field.handleChange(
                                        field.state.value.filter((r) => r !== restriction.value)
                                      );
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">{t(restriction.label)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="bringFriend">
                      {(field) => (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-1">
                            <input
                              type="checkbox"
                              id="bringFriend"
                              checked={field.state.value}
                              onChange={(e) => field.handleChange(e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <Label htmlFor="bringFriend">{t("booking.preferences.bringFriend")}</Label>
                          </div>
                        </div>
                      )}
                    </form.Field>
                  </>
                )}
              </Box> */}

              {/* Payment Method */}
              <form.Field
                name="paymentMethod"
                validators={{
                  onChange: ({ value }) =>
                    value === PaymentMethodEnum.CREDIT && (userCredits?.balance ?? 0) < subtotal
                      ? "Không đủ xu"
                      : undefined
                }}
              >
                {(field) => (
                  <PaymentMethod
                    value={field.state.value}
                    onChanged={(value) => field.handleChange(value)}
                    totalAmount={total}
                    error={field.state.meta.errors?.[0] || undefined}
                  />
                )}
              </form.Field>
            </div>

            {/* Right Column - Booking Summary */}
            <div className="md:col-span-2 lg:col-span-1 sticky top-[90px] self-start">
              <Box className="space-y-4">
                <p className="text-text-primary font-normal text-responsive-md">
                  {t("booking.summary.title")}
                </p>

                {/* Line Items */}
                <div className="space-y-1 text-text-primary text-responsive-base">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary">
                      {selectedTicketType.title} × {quantity}
                    </span>
                    <span>
                      {subtotal === 0
                        ? t("booking.tickets.free")
                        : paymentMethod === PaymentMethodEnum.CREDIT
                          ? formatCredit(subtotal)
                          : `${formatCurrency(subtotal * CREDIT_PRICE)}`}
                    </span>
                  </div>

                  {bringFriend && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-secondary">{t("booking.tickets.bringFriend")}</span>
                      <span>{formatCurrency(bringFriend ? subtotal * 0.5 * CREDIT_PRICE : 0)}</span>
                    </div>
                  )}

                  {serviceFee > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-secondary">
                        {t("booking.tickets.serviceFee", { percentage: SERVICE_FEE_PRICE * 100 })}
                      </span>
                      <span className="line-through text-red-500">
                        {paymentMethod === PaymentMethodEnum.CREDIT
                          ? formatCredit(serviceFee)
                          : formatCurrency(serviceFee * CREDIT_PRICE)}
                      </span>
                    </div>
                  )}
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-responsive-md">{t("booking.tickets.total")}</span>
                  <span className="text-text-primary text-responsive-md font-normal">
                    {total === 0
                      ? t("booking.tickets.free")
                      : paymentMethod === PaymentMethodEnum.CREDIT
                        ? formatCredit(total)
                        : `${formatCurrency(total * CREDIT_PRICE)}`}
                  </span>
                </div>

                <Separator />

                {/* Confirm Button */}
                <div className="space-y-3">
                  <Button className="w-full" onClick={() => form.handleSubmit()} disabled={isProcessing}>
                    {isProcessing
                      ? t("booking.summary.processing")
                      : total === 0
                        ? t("booking.summary.confirmBooking")
                        : t("booking.summary.confirmAndPay")}
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate({ to: `/event/${event.id}` });
                    }}
                    disabled={isProcessing}
                  >
                    {t("common.cancel")}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    {t("booking.summary.termsAndConditions")}
                  </p>
                </div>
              </Box>
              <div className="mt-4 text-center">
                <a href="#" className="text-muted-foreground hover:text-foreground underline">
                  {t("booking.summary.needHelp")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </form>
    </RawLayout>
  );
}
